'use client';

import type { ImgHTMLAttributes } from 'react';
import styled from 'styled-components';
import { Text } from '@lmn/design-system';
import { BASE_PATH } from '@/app/components/search/utils/path';
import customCss from '@/app/components/hotel-detail/css/customCss';
import { useTranslation } from '@/app/components/translations/Translation';

// Rendered TripAdvisor branding height, in px. Single knob — change this to resize the standalone owl
// on every surface (search cards, hero summaries, gallery widget, reviews section, modal, popover)
// AND the attribution wordmark below. We use our internal 16px design height and intentionally do NOT
// follow TripAdvisor's 20px brand minimum.
export const OWL_HEIGHT_PX = 16;

// Canonical Ollie owl for aggregate guest-rating displays. Sized by HEIGHT with width auto — the
// SVG viewBox is cropped to the glyph, so the rendered height IS the owl height (the owl is ~1.56×
// wider than tall). Single source so every surface stays in lockstep; callers must not override the
// size (pass only spacing/alt), otherwise they break the lockstep.
const OwlImg = styled.img`
  height: ${OWL_HEIGHT_PX}px;
  width: auto;
  flex-shrink: 0;
`;

type TripAdvisorOwlProps = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'width' | 'height'>;

export default function TripAdvisorOwl({ alt = '', ...rest }: TripAdvisorOwlProps) {
  return <OwlImg src={`${BASE_PATH}/img/icons/tripadvisor-owl.svg`} alt={alt} {...rest} />;
}

// Attribution row for the reviews section/modal headers: the required "Traveller Reviews brought to
// you by" caption + the combined Ollie-owl + "Tripadvisor" wordmark asset. Desktop only — on mobile
// the standalone owl in the score row carries the TA branding, and the text + wordmark would overflow
// the narrow header. The wordmark height tracks OWL_HEIGHT_PX so all TA branding stays in lockstep.
const AttributionRow = styled.div`
  display: none;
  align-items: center;
  gap: 6px;

  @media ${customCss.breakpoints.sm} {
    display: flex;
  }
`;

const BrandWordmark = styled.img`
  height: ${OWL_HEIGHT_PX}px;
  width: auto;
  flex-shrink: 0;
`;

export function TripAdvisorAttribution({ className }: { className?: string }) {
  const { t } = useTranslation();

  return (
    <AttributionRow className={className}>
      <Text variant="Caption" color="textSecondary">
        {t('vacation.select.details.guest.reviews.tripadvisor.attribution') || 'Traveller Reviews brought to you by'}
      </Text>
      <BrandWordmark src={`${BASE_PATH}/img/icons/tripadvisor_ollie_brand_text.svg`} alt="Tripadvisor" />
    </AttributionRow>
  );
}
